import type { FC } from "react";

import { motion } from "framer-motion";

import { formatTokensCompact } from "../../utils/currency";

export type HudSparklineTone =
  | "success"
  | "info"
  | "warning"
  | "danger"
  | "neutral";

interface ToneClasses {
  /** Tailwind text-color class for the bar fill (uses currentColor in SVG). */
  fill: string;
  /** Baseline grid stroke. */
  baseline: string;
}

const TONES: Record<HudSparklineTone, ToneClasses> = {
  danger: {
    baseline: "text-rose-700/30 dark:text-rose-400/20",
    fill: "text-rose-500/70 dark:text-rose-400/70",
  },
  info: {
    baseline: "text-sky-700/30 dark:text-sky-400/20",
    fill: "text-sky-500/70 dark:text-sky-400/70",
  },
  neutral: {
    baseline: "text-slate-700/30 dark:text-slate-500/20",
    fill: "text-slate-500/70 dark:text-slate-400/60",
  },
  success: {
    baseline: "text-emerald-700/30 dark:text-emerald-400/20",
    fill: "text-emerald-500/70 dark:text-emerald-400/70",
  },
  warning: {
    baseline: "text-amber-700/30 dark:text-amber-400/20",
    fill: "text-amber-500/70 dark:text-amber-400/70",
  },
};

interface HudSparklineProps {
  values: number[];
  tone?: HudSparklineTone;
  /** Render width in pixels. Bar count = values.length. */
  width?: number;
  /** Render height in pixels. Default 28. */
  height?: number;
  /** Minimum bar gap in pixels — wider gaps when fewer bars. */
  barGap?: number;
  className?: string;
}

/**
 * Tone-driven mini-chart with spring-revealed bars in the HUD-CRT dialect.
 *
 * Rendered as an inline SVG. Each value becomes a column whose height is
 * proportional to value/max; bars animate in via framer-motion spring with
 * a 20ms stagger per bar (left-to-right). Hover any bar to surface a native
 * <title> tooltip with the value formatted as `Xk` (or raw int < 1000) in
 * monospace tabular-nums.
 *
 * Tone is the only color knob — driven by the consumer's decision-hint state
 * (success / info / warning / danger). A faint baseline is rendered at y=h
 * so the chart reads "this is a meter" not "this is a logo." Empty values
 * arrays render the baseline with no bars (still occupies the height so
 * surrounding layout doesn't jitter).
 */
export const HudSparkline: FC<HudSparklineProps> = ({
  values,
  tone = "neutral",
  width = 120,
  height = 28,
  barGap = 1,
  className,
}) => {
  const toneClasses = TONES[tone];
  const maxValue = values.reduce((acc, v) => Math.max(acc, v), 0);
  const barCount = values.length;
  const totalGap = barCount > 0 ? barGap * Math.max(barCount - 1, 0) : 0;
  const barWidth =
    barCount > 0 ? Math.max((width - totalGap) / barCount, 1) : 0;

  return (
    <svg
      aria-label={`Sparkline (${barCount} samples, max ${formatTokensCompact(maxValue)})`}
      className={className}
      height={height}
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      width={width}
    >
      <line
        className={toneClasses.baseline}
        stroke="currentColor"
        strokeWidth={1}
        x1={0}
        x2={width}
        y1={height - 0.5}
        y2={height - 0.5}
      />
      {barCount > 0 &&
        values.map((value, idx) => {
          const x = idx * (barWidth + barGap);
          const normalized = maxValue > 0 ? value / maxValue : 0;
          const barHeight = Math.max(normalized * (height - 1), 1);
          const y = height - barHeight;
          return (
            <motion.rect
              key={idx}
              animate={{ scaleY: 1 }}
              className={toneClasses.fill}
              fill="currentColor"
              height={barHeight}
              initial={{ scaleY: 0 }}
              style={{ transformOrigin: `${x + barWidth / 2}px ${height}px` }}
              transition={{
                damping: 22,
                delay: idx * 0.02,
                stiffness: 260,
                type: "spring",
              }}
              width={barWidth}
              x={x}
              y={y}
            >
              <title>{formatTokensCompact(value)}</title>
            </motion.rect>
          );
        })}
    </svg>
  );
};
